import { useMemo, useState } from 'react';

function isRowArray(data: unknown): data is Array<Record<string, unknown>> {
  return (
    Array.isArray(data) &&
    data.length > 0 &&
    data.every((r) => typeof r === 'object' && r !== null && !Array.isArray(r))
  );
}

/** Render an arbitrary value as a cell string. Objects → JSON; null/undefined → ''. */
function cell(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * Generic, domain-free source-rows table. Given `data` that is a non-empty array
 * of plain objects, renders a collapsed-by-default table whose columns are the
 * union of row keys (ordered by first appearance). Anything else renders nothing.
 */
export function SourceRows({ data }: { data?: unknown }) {
  const [open, setOpen] = useState(false);

  const columns = useMemo(() => {
    if (!isRowArray(data)) return [];
    const seen: string[] = [];
    for (const row of data) {
      for (const key of Object.keys(row)) {
        if (!seen.includes(key)) seen.push(key);
      }
    }
    return seen;
  }, [data]);

  if (!isRowArray(data)) return null;

  return (
    <div className="ra-source-rows mt-2 text-xs">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="text-neutral-500 hover:text-neutral-700 underline underline-offset-2"
      >
        {open ? 'Hide' : 'Show'} source rows ({data.length})
      </button>
      {open && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th
                    key={c}
                    className="border-b border-neutral-200 px-2 py-1 text-left font-medium text-neutral-600"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <td key={c} className="border-b border-neutral-100 px-2 py-1 align-top">
                      {cell(row[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
